import { apiUrl } from './api-url';
// Track 1 (parallel branch) is shipping `lib/inAppBrowser.ts` with a
// `detectInAppBrowser()` helper that returns the in-app browser kind
// (facebook | instagram | tiktok | snapchat | twitter | linkedin |
// pinterest) or null. We import it eagerly here; at the time this commit
// lands the file may not yet exist locally — the merge into the shared
// branch will resolve it. TypeScript may complain in isolation; that is
// expected and noted in the Track 2 spec.
import { detectInAppBrowser } from './inAppBrowser';
import { trackPushOptInConversion } from './installAttribution';

export type PermissionStatus = 'unknown' | 'granted' | 'denied' | 'dismissed';

const PERMISSION_KEY = 'papacito_notification_permission';

export function getStoredPermission(): PermissionStatus {
  return (localStorage.getItem(PERMISSION_KEY) as PermissionStatus) || 'unknown';
}

export async function requestPermission(): Promise<PermissionStatus> {
  if (!('Notification' in window)) return 'denied';
  if (Notification.permission === 'granted') {
    localStorage.setItem(PERMISSION_KEY, 'granted');
    return 'granted';
  }
  if (Notification.permission === 'denied') {
    localStorage.setItem(PERMISSION_KEY, 'denied');
    return 'denied';
  }
  try {
    const permission = await Notification.requestPermission();
    localStorage.setItem(PERMISSION_KEY, permission);
    return permission as PermissionStatus;
  } catch {
    return 'denied';
  }
}

function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

// 2026-05-24 Diagnostic-only: capture the Service Worker registration / controller
// state at the moment of a push-subscribe failure. Embedded in
// push_prompt_events.context_metadata to let us see WHY serviceWorker.ready
// hangs for Android Chrome users (1.64% opt-in vs 20% target). Pure read,
// JSON-serializable, defensive against the no-SW feature-detect branch.
async function captureSwDiagState(): Promise<Record<string, unknown>> {
  try {
    const hasSwApi = typeof navigator !== 'undefined' && 'serviceWorker' in navigator;
    const reg = hasSwApi
      ? await navigator.serviceWorker.getRegistration().catch(() => null)
      : null;
    return {
      hasController: hasSwApi ? !!navigator.serviceWorker.controller : false,
      controllerState: hasSwApi ? (navigator.serviceWorker.controller?.state ?? null) : null,
      hasRegistration: !!reg,
      activeState: reg?.active?.state ?? null,
      installingState: reg?.installing?.state ?? null,
      waitingState: reg?.waiting?.state ?? null,
      scope: reg?.scope ?? null,
      isStandalone:
        typeof window !== 'undefined' && typeof window.matchMedia === 'function'
          ? window.matchMedia('(display-mode: standalone)').matches
          : false,
      hostname: typeof window !== 'undefined' ? window.location.hostname : null,
    };
  } catch {
    return { swDiagCaptureFailed: true };
  }
}

export interface SubscribeToPushResult {
  success: boolean;
  reason?:
    | 'push_not_supported'
    | 'ios_not_installed'
    | 'ios_safari_not_pwa'
    | 'ios_version_too_old'
    | 'permission_denied'
    | 'permission_default'
    | 'missing_vapid_key'
    | 'invalid_subscription_object'
    | 'server_save_failed'
    | 'unknown_error'
    // 2026-05-01 Track 2: in-app browser short-circuit. FB/IG/TikTok/etc.
    // WebViews strip the Notification API; attempting to subscribe silently
    // fails. We early-exit BEFORE burning the OS prompt and tag the failure
    // with the specific in-app browser kind so analytics can quantify the
    // recovery opportunity.
    | 'inapp_browser_facebook'
    | 'inapp_browser_instagram'
    | 'inapp_browser_tiktok'
    | 'inapp_browser_snapchat'
    | 'inapp_browser_twitter'
    | 'inapp_browser_linkedin'
    | 'inapp_browser_pinterest';
  status?: number;
  serverMessage?: string;
}

// Diagnostic variant labels recognized by the backend `/api/push/prompt-event`
// route. Mirrors PUSH_PROMPT_VARIANTS in lib/db schema. Kept as a string union
// here (no cross-package import) so this lib stays self-contained.
type DiagnosticVariant =
  | 'A_likes_blur'
  | 'B_first_match'
  | 'C_exit_intent'
  | 'D_welcome'
  | 'E_first_message';

// Snapshot enum recognized server-side. We intentionally include 'unsupported'
// for browsers without the Notification API at all, even though we early-return
// on `push_not_supported` before logging in that case.
type PermissionStateSnapshot = 'default' | 'granted' | 'denied' | 'unsupported';

function detectPlatform(): 'ios_pwa' | 'android_pwa' | 'desktop' | 'mobile_web' {
  if (typeof window === 'undefined') return 'desktop';
  const ua = navigator.userAgent || '';
  const isIOS = /iphone|ipad|ipod/i.test(ua);
  const isAndroid = /android/i.test(ua);
  const standalone =
    (typeof window.matchMedia === 'function' && window.matchMedia('(display-mode: standalone)').matches) ||
    ('standalone' in navigator && (navigator as { standalone?: boolean }).standalone === true);
  if (isIOS) return standalone ? 'ios_pwa' : 'mobile_web';
  if (isAndroid) return standalone ? 'android_pwa' : 'mobile_web';
  return 'desktop';
}

function isInstalledPwa(): boolean {
  if (typeof window === 'undefined') return false;
  if (typeof window.matchMedia === 'function' && window.matchMedia('(display-mode: standalone)').matches) return true;
  if ('standalone' in navigator && (navigator as { standalone?: boolean }).standalone === true) return true;
  return false;
}

function readPermissionState(): PermissionStateSnapshot {
  if (typeof window === 'undefined' || !('Notification' in window)) return 'unsupported';
  const p = Notification.permission;
  if (p === 'granted' || p === 'denied' || p === 'default') return p;
  return 'default';
}

/**
 * iOS Safari outside an installed PWA cannot deliver web push at all. Apple
 * only ships the `pushManager.subscribe()` path inside a home-screen install.
 * In a regular Safari tab the permission prompt either silently fails or
 * grants but `subscribe()` later throws / returns a dead endpoint.
 *
 * We detect this case so the opt-in flow can fail fast instead of burning
 * the OS prompt and leaving permission in a half-granted state. The iOS
 * install gate (handled elsewhere) takes over from here.
 */
export function isIosSafariNotInPwa(): boolean {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || '';
  if (!/iPhone|iPad|iPod/.test(ua)) return false;
  const standalone =
    ('standalone' in navigator && (navigator as { standalone?: boolean }).standalone === true) ||
    (typeof window.matchMedia === 'function' && window.matchMedia('(display-mode: standalone)').matches);
  return !standalone;
}

interface DiagnosticPayload {
  token: string | null | undefined;
  variant: DiagnosticVariant;
  eventType: 'subscribe_failed' | 'permission_state_at_check';
  gateReason?: string;
  permissionState?: PermissionStateSnapshot;
  contextMetadata?: Record<string, unknown> | null;
}

/**
 * Best-effort POST to the diagnostic endpoint added 2026-05-01 to debug the
 * 0% opt-in rate. Never throws — analytics failures must not break the opt-in
 * flow itself. Tolerates older backends that don't yet recognize the new
 * eventType / fields (Track 1 may not be deployed at the moment we run).
 */
async function logDiagnostic(payload: DiagnosticPayload): Promise<void> {
  try {
    if (!payload.token) return;
    // 2026-05-01 Track 2: always inject navigator.userAgent into
    // contextMetadata so we can identify which browsers (Discord WebView,
    // Telegram, Snapchat configs we missed, etc.) are firing the various
    // gate_reasons. Spread BEFORE caller-supplied metadata so callers can
    // override if they ever need to (they typically won't).
    const ua = typeof navigator !== 'undefined' ? navigator.userAgent : null;
    const callerMeta = payload.contextMetadata ?? {};
    const mergedContextMetadata: Record<string, unknown> = {
      userAgent: ua,
      ...callerMeta,
    };
    const body = {
      variant: payload.variant,
      eventType: payload.eventType,
      platform: detectPlatform(),
      isInstalled: isInstalledPwa(),
      contextMetadata: mergedContextMetadata,
      gateReason: payload.gateReason,
      permissionState: payload.permissionState,
    };
    await fetch(apiUrl('/api/push/prompt-event'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${payload.token}`,
      },
      body: JSON.stringify(body),
      // Use keepalive so the request survives if the user navigates away
      // immediately after a failure (common on the iOS Safari path).
      keepalive: true,
    });
  } catch {
    // Swallow — diagnostic logging must never crash the opt-in flow.
  }
}

export async function subscribeToPush(
  token: string,
  variant: DiagnosticVariant = 'D_welcome',
): Promise<SubscribeToPushResult> {
  // Snapshot the OS permission state at the moment the user clicked "Enable".
  // Useful diagnostic for splitting the funnel: a click coming in with
  // permission already 'denied' means the OS prompt was burned previously.
  const permissionAtCheck = readPermissionState();
  void logDiagnostic({
    token,
    variant,
    eventType: 'permission_state_at_check',
    permissionState: permissionAtCheck,
  });

  // 2026-05-01 Track 2: in-app browser short-circuit. Prod data showed 24
  // `gate_skipped / no_notification_api` events from 2 distinct users in 24h
  // that all came from FB/IG/TikTok WebViews. These environments either
  // strip the Notification API entirely or hand back a stub that resolves
  // 'granted' but fails on `pushManager.subscribe()`. Either way, calling
  // `Notification.requestPermission()` here would burn the OS prompt for
  // zero benefit. Fail fast with a specific gate_reason so the new
  // overlay (Track 1) can take over and tell the user to open in real
  // Safari/Chrome. The detector itself is in `lib/inAppBrowser.ts`.
  const inApp = detectInAppBrowser();
  if (inApp) {
    const gateReason = `inapp_browser_${inApp}` as const;
    console.log('[Push] early-return: inapp_browser', { inApp });
    void logDiagnostic({
      token,
      variant,
      eventType: 'subscribe_failed',
      gateReason,
      // 'unsupported' is the closest semantic fit — the API may technically
      // exist on `window` in some WebViews but is functionally dead.
      permissionState: 'unsupported',
    });
    return { success: false, reason: gateReason };
  }

  if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
    console.log('[Push] early-return: no_service_worker', {
      hasSW: 'serviceWorker' in navigator,
      hasPushManager: 'PushManager' in window,
    });
    const swDiagState = await captureSwDiagState();
    void logDiagnostic({
      token,
      variant,
      eventType: 'subscribe_failed',
      gateReason: 'no_service_worker',
      permissionState: permissionAtCheck,
      contextMetadata: swDiagState,
    });
    return { success: false, reason: 'push_not_supported' };
  }

  const isIOS = /iphone|ipad|ipod/i.test(navigator.userAgent);
  const isStandalone =
    ('standalone' in navigator && (navigator as { standalone?: boolean }).standalone === true) ||
    window.matchMedia('(display-mode: standalone)').matches;

  console.log(`[Push] isIOS: ${isIOS} isStandalone: ${isStandalone}`);

  // Speculative fix (2026-05-01): iOS Safari outside an installed PWA cannot
  // actually deliver web push. Apple only ships the API inside a home-screen
  // install. Calling Notification.requestPermission() in that environment
  // either silently fails or grants but pushManager.subscribe() later throws,
  // burning the prompt for no benefit. Fail fast and let the iOS install gate
  // path take over. This should recover several of the "vanished accepts"
  // where iOS Safari users click Enable then nothing happens.
  if (isIosSafariNotInPwa()) {
    console.log('[Push] early-return: ios_safari_not_pwa');
    void logDiagnostic({
      token,
      variant,
      eventType: 'subscribe_failed',
      gateReason: 'ios_safari_not_pwa',
      permissionState: permissionAtCheck,
    });
    return { success: false, reason: 'ios_safari_not_pwa' };
  }

  if (isIOS && !isStandalone) {
    console.log('[Push] early-return: ios_not_installed (iOS UA, not standalone)');
    void logDiagnostic({
      token,
      variant,
      eventType: 'subscribe_failed',
      gateReason: 'ios_safari_not_pwa',
      permissionState: permissionAtCheck,
    });
    return { success: false, reason: 'ios_not_installed' };
  }

  if (isIOS) {
    const match = navigator.userAgent.match(/OS (\d+)_/);
    const iosVersion = match ? parseInt(match[1], 10) : 0;
    if (iosVersion < 16) {
      console.log('[Push] early-return: ios_version_too_old', { iosVersion });
      void logDiagnostic({
        token,
        variant,
        eventType: 'subscribe_failed',
        gateReason: 'ios_version_too_old',
        permissionState: permissionAtCheck,
        contextMetadata: { ios_version: iosVersion },
      });
      return { success: false, reason: 'ios_version_too_old' };
    }
  }

  try {
    // serviceWorker.ready never rejects — it resolves only when there's an
    // active SW. If registration silently failed (e.g. /sw.js 404, host not in
    // isPwaHost() allow-list) this hangs forever and the user perceives "the
    // Allow button does nothing." Race against a 5s timeout so we surface a
    // concrete failure instead of hanging the click handler.
    console.log('[Push] awaiting serviceWorker.ready (with 5s timeout)...');
    let registration: ServiceWorkerRegistration | undefined;
    try {
      registration = await Promise.race([
        navigator.serviceWorker.ready,
        new Promise<undefined>((_, reject) =>
          setTimeout(() => reject(new Error('serviceWorker.ready timeout')), 5000),
        ),
      ]);
    } catch (readyErr) {
      const errMsg = readyErr instanceof Error ? readyErr.message : String(readyErr);
      console.error('[Push] early-return: serviceWorker.ready timed out or threw:', errMsg);
      const swDiagState = await captureSwDiagState();
      void logDiagnostic({
        token,
        variant,
        eventType: 'subscribe_failed',
        gateReason: 'service_worker_ready_timeout',
        permissionState: readPermissionState(),
        contextMetadata: { error_message: errMsg.slice(0, 200), ...swDiagState },
      });
      return { success: false, reason: 'push_not_supported' };
    }

    // Edge case: serviceWorker.ready can resolve to undefined in some browsers
    // when no SW is registered. Guard so we don't NPE on .pushManager.
    if (!registration) {
      console.log('[Push] early-return: serviceWorker.ready resolved to undefined');
      const swDiagState = await captureSwDiagState();
      void logDiagnostic({
        token,
        variant,
        eventType: 'subscribe_failed',
        gateReason: 'no_service_worker',
        permissionState: readPermissionState(),
        contextMetadata: swDiagState,
      });
      return { success: false, reason: 'push_not_supported' };
    }

    console.log('[Push] calling Notification.requestPermission()...');
    const permission = await Notification.requestPermission();
    console.log(`[Push] Permission result: ${permission}`);
    if (permission === 'denied') {
      console.log('[Push] early-return: permission_request_denied (user picked Block, or browser auto-denied because pre-state was already denied — no OS prompt would have appeared)');
      void logDiagnostic({
        token,
        variant,
        eventType: 'subscribe_failed',
        gateReason: 'permission_request_denied',
        permissionState: 'denied',
      });
      return { success: false, reason: 'permission_denied' };
    }
    if (permission !== 'granted') {
      // 'default' — user dismissed the OS prompt without choosing.
      console.log('[Push] early-return: permission_request_default (user dismissed OS prompt)');
      void logDiagnostic({
        token,
        variant,
        eventType: 'subscribe_failed',
        gateReason: 'permission_request_default',
        permissionState: 'default',
      });
      return { success: false, reason: 'permission_default' };
    }

    const VAPID_PUBLIC_KEY = import.meta.env.VITE_VAPID_PUBLIC_KEY;
    if (!VAPID_PUBLIC_KEY) {
      console.log('[Push] early-return: no_vapid_key (VITE_VAPID_PUBLIC_KEY missing at build time)');
      void logDiagnostic({
        token,
        variant,
        eventType: 'subscribe_failed',
        gateReason: 'no_vapid_key',
        permissionState: 'granted',
      });
      return { success: false, reason: 'missing_vapid_key' };
    }

    const convertedKey = urlBase64ToUint8Array(VAPID_PUBLIC_KEY);
    let subscription: PushSubscription;
    try {
      subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: convertedKey as BufferSource,
      });
    } catch (subErr) {
      // pushManager.subscribe() throws on iOS Safari (non-PWA), Brave with
      // hardened settings, and when the VAPID key bytes don't match the
      // server. Log the error message (capped to 120 chars by server) so we
      // can spot patterns in the dashboard.
      const errMsg = subErr instanceof Error ? subErr.message : String(subErr);
      console.error('[Push] pushManager.subscribe threw:', errMsg);
      void logDiagnostic({
        token,
        variant,
        eventType: 'subscribe_failed',
        gateReason: 'pushmanager_subscribe_threw',
        permissionState: 'granted',
        contextMetadata: { error_message: errMsg.slice(0, 200) },
      });
      return { success: false, reason: 'unknown_error' };
    }

    console.log(`[Push] Subscription endpoint: ${subscription.endpoint.substring(0, 60)}...`);

    const subJson = subscription.toJSON();

    if (!subJson.endpoint || !subJson.keys?.p256dh || !subJson.keys?.auth) {
      console.log('[Push] early-return: invalid_subscription_object', {
        hasEndpoint: !!subJson.endpoint,
        hasP256dh: !!subJson.keys?.p256dh,
        hasAuth: !!subJson.keys?.auth,
      });
      void logDiagnostic({
        token,
        variant,
        eventType: 'subscribe_failed',
        gateReason: 'invalid_subscription_object',
        permissionState: 'granted',
      });
      return { success: false, reason: 'invalid_subscription_object' };
    }

    const fetchUrl = apiUrl('/users/push-subscription');
    console.log('[Push] Fetching to URL:', fetchUrl);

    let res: Response;
    try {
      res = await fetch(fetchUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ subscription: subJson }),
      });
    } catch (postErr) {
      // Network error — DNS, offline, CORS preflight failure, etc. The user's
      // browser DOES have a live PushSubscription at this point but we never
      // told the server about it. This is one of the silent-failure modes
      // we've been blind to.
      const errMsg = postErr instanceof Error ? postErr.message : String(postErr);
      console.error('[Push] Server POST threw:', errMsg);
      void logDiagnostic({
        token,
        variant,
        eventType: 'subscribe_failed',
        gateReason: 'server_post_threw',
        permissionState: 'granted',
        contextMetadata: { error_message: errMsg.slice(0, 200) },
      });
      return { success: false, reason: 'server_save_failed' };
    }

    console.log(`[Push] Server save response status: ${res.status}`);

    if (!res.ok) {
      let text = '';
      try { text = await res.text(); } catch { /* ignore */ }
      console.error('[Push] Server error response:', res.status, text);
      void logDiagnostic({
        token,
        variant,
        eventType: 'subscribe_failed',
        gateReason: 'server_post_failed',
        permissionState: 'granted',
        contextMetadata: {
          status: res.status,
          body_excerpt: text ? text.slice(0, 200) : null,
        },
      });
      return {
        success: false,
        reason: 'server_save_failed',
        status: res.status,
        serverMessage: text || undefined,
      };
    }

    // Push subscription saved successfully → fire ad-attribution conversion.
    // Dedupe + ad-attribution gating happen inside trackPushOptInConversion.
    try { trackPushOptInConversion(); } catch (err) {
      console.warn('[Push] trackPushOptInConversion failed (non-blocking):', err);
    }

    return { success: true };
  } catch (err) {
    console.error('[Push] subscribeToPush error:', err);
    const errMsg = err instanceof Error ? err.message : String(err);
    void logDiagnostic({
      token,
      variant,
      eventType: 'subscribe_failed',
      gateReason: 'unknown_error',
      permissionState: readPermissionState(),
      contextMetadata: { error_message: errMsg.slice(0, 200) },
    });
    return { success: false, reason: 'unknown_error' };
  }
}
